import * as z from "zod";
import { codeName, flag, pagination } from "../index";
import { bcRecordKinds, bcUnmatchedReasons } from "./enums";
import { organizationRef } from "./fields";

// The customers and the vendors as Business Central has them (50-api/party.md §2.9; I-27, ADR-108, ADR-116): the
// latest state of each, and the organization it is of. One that matches nobody waits, and Tomorrow matches it by hand.

const bcRecordFilters = {
  kind: z.enum(bcRecordKinds).optional(),
  /** Only the ones no organization has yet: the queue of who matches them. */
  waiting: flag.optional(),
  /** Part of the name, of the number or of the VAT number, case-insensitive. */
  q: z.string().trim().min(1).max(100).optional(),
  /** The records of the organization: its customer and its vendor, with the block Business Central has. */
  organizationId: z.uuid().optional(),
  /**
   * The waiting records that could be the organization's: the same VAT number, or the same name, compared as the
   * duplicates are. Before a first send one of them is matched instead, or Business Central gets a second record.
   */
  possibleFor: z.uuid().optional(),
};

/** The waiting ones the oldest first; the others by name. */
export const listBcRecordsInput = z.object({ ...bcRecordFilters, ...pagination({ defaultSize: 25, maxSize: 100 }) });
export type ListBcRecordsInput = z.input<typeof listBcRecordsInput>;

/** How many records the list has with the same filters, and since when the oldest waits (ADR-086). */
export const countBcRecordsInput = z.object(bcRecordFilters);
export type CountBcRecordsInput = z.input<typeof countBcRecordsInput>;

/**
 * A record that waits becomes the record of an organization with that role, which has no other one of its kind: what
 * the consumer does when it finds the organization itself.
 */
export const matchBcRecordInput = z.object({ recordId: z.uuid(), organizationId: z.uuid() });
export type MatchBcRecordInput = z.input<typeof matchBcRecordInput>;
export const bcRecordParams = matchBcRecordInput.pick({ recordId: true });
export const matchBcRecordBody = matchBcRecordInput.omit({ recordId: true });

export const bcRecordDto = z
  .object({
    id: z.uuid(),
    /** Code of the company of the group the record is of on Business Central. */
    company: z.string(),
    kind: z.enum(bcRecordKinds),
    /** No. of the record on Business Central. */
    no: z.string(),
    name: z.string(),
    vatNumber: z.string().nullable().optional(),
    /** The block Business Central has on the record, in its code; null, none. */
    blocked: z.string().nullable().optional(),
    /** Payment terms as Business Central names them, and the terms of Nexxt that code stands for. */
    paymentTermsCode: z.string().nullable().optional(),
    paymentTerms: codeName.nullable().optional(),
    organization: organizationRef.nullable(),
    /** Why it has no organization; null once it has one. */
    unmatchedReason: z.enum(bcUnmatchedReasons).nullable(),
    /** `updatedAt` of the state kept, as Business Central gives it. */
    bcUpdatedAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: "BcRecord" });
